// The volume basis is averaged over a period of calendar months. Months are
// "YYYY-MM" strings so they sort and compare lexicographically, which lets the
// rest of the tool treat a period as a pair of plain bounds.
//
// Every month in a period contributes a whole month's worth of volume. The one
// that may still be in progress is scaled up to the rate it is on course for,
// because averaging its part-month takings against whole months is what pulled
// the figures down before the period was selectable at all.

/** Inclusive number of months a period may span. */
export const MAX_PERIOD_MONTHS = 12;

export interface MonthPeriod {
  /** Inclusive bound, "YYYY-MM". */
  start: string;
  /** Inclusive bound, "YYYY-MM". */
  end: string;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// monthIndex is 0-based and allowed to fall outside 0-11 so callers can do
// plain arithmetic on it and let the year carry.
function toMonth(year: number, monthIndex: number): string {
  const carriedYear = year + Math.floor(monthIndex / 12);
  const normalized = ((monthIndex % 12) + 12) % 12;
  return `${carriedYear}-${pad2(normalized + 1)}`;
}

function parts(month: string): { year: number; monthIndex: number } {
  const [year, month1] = month.split("-");
  return { year: Number(year), monthIndex: Number(month1) - 1 };
}

export function addMonths(month: string, delta: number): string {
  const { year, monthIndex } = parts(month);
  return toMonth(year, monthIndex + delta);
}

// "Today" is read in UTC because the dates these helpers produce are handed
// straight to the insights endpoint, which parses them as UTC. Reading the
// local clock instead would let a browser east of Greenwich ask, in the hours
// after local midnight, for a day UTC has not finished — projecting a week's
// takings from six days and change, and overstating the rate by a percent or
// two. It also keeps these functions independent of the machine's timezone.

export function currentMonth(today: Date = new Date()): string {
  return toMonth(today.getUTCFullYear(), today.getUTCMonth());
}

export function lastCompleteMonth(today: Date = new Date()): string {
  return toMonth(today.getUTCFullYear(), today.getUTCMonth() - 1);
}

/** Days of `today`'s month that have fully elapsed. Today itself is partial. */
export function elapsedDays(today: Date = new Date()): number {
  return today.getUTCDate() - 1;
}

export function daysInMonth(month: string): number {
  const { year, monthIndex } = parts(month);
  // Day 0 of the following month is the last day of this one.
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/** A projection is made from whole weeks, so this is also the minimum. */
export const PROJECTION_WEEK = 7;

/**
 * Complete days of the current month a projection may be built from: whole
 * weeks only, discarding up to six of the freshest days.
 *
 * Takings are not spread evenly across the days of the week, so a sample that
 * is not a whole number of weeks over-weights whichever days it happens to
 * contain. Scaling the first 11 days of this October up to 31 overstates a
 * business that takes its money at weekends by 25%, because 4 of those 11 days
 * are weekend days where only 9 of the month's 31 are. Any whole number of
 * weeks carries the same small residual instead — about 1.6% here, from the
 * month itself not being a whole number of weeks — so one week is already more
 * accurate than eleven days, and the sample still grows every Sunday.
 */
export function projectableDays(today: Date = new Date()): number {
  return Math.floor(elapsedDays(today) / PROJECTION_WEEK) * PROJECTION_WEEK;
}

export function isCurrentMonthProjectable(today: Date = new Date()): boolean {
  return projectableDays(today) >= PROJECTION_WEEK;
}

/**
 * The factor turning a month-in-progress's totals into a full-month rate, or
 * null for any month that needs no scaling — a month already complete, or a
 * current month too young to project from.
 *
 * Every decision about the in-progress month routes through this one predicate:
 * the end date we ask the server for and the factor we scale the answer by have
 * to agree, or we scale a number of days we did not request.
 */
export function projectionFactor(
  month: string,
  today: Date = new Date()
): number | null {
  if (month !== currentMonth(today)) return null;
  const days = projectableDays(today);
  if (days < PROJECTION_WEEK) return null;
  return daysInMonth(month) / days;
}

/**
 * Latest month a period may end on. The current month counts once enough of it
 * has elapsed to project from — leaving it out entirely would mean a merchant
 * whose whole history is this month shows no data at all, and would put every
 * figure in the tool up to a month behind.
 */
export function latestSelectableMonth(today: Date = new Date()): string {
  return isCurrentMonthProjectable(today)
    ? currentMonth(today)
    : lastCompleteMonth(today);
}

export function defaultPeriod(today: Date = new Date()): MonthPeriod {
  const end = latestSelectableMonth(today);
  return { start: addMonths(end, -(MAX_PERIOD_MONTHS - 1)), end };
}

/** Inclusive, so a period of one month spans 1. Zero or negative when reversed. */
export function monthSpan(period: MonthPeriod): number {
  const from = parts(period.start);
  const to = parts(period.end);
  return (to.year - from.year) * 12 + (to.monthIndex - from.monthIndex) + 1;
}

export function isValidPeriod(
  period: MonthPeriod,
  today: Date = new Date()
): boolean {
  const span = monthSpan(period);
  return (
    span >= 1 &&
    span <= MAX_PERIOD_MONTHS &&
    period.end <= latestSelectableMonth(today)
  );
}

// A stored period is honoured only while it still makes sense. Null bounds, a
// reversed or over-long span, or an end that has run past the latest selectable
// month all fall back to the default rather than querying a period nobody
// chose — which is what a persisted value from an earlier session can become.
export function resolvePeriod(
  start: string | null,
  end: string | null,
  today: Date = new Date()
): MonthPeriod {
  if (!start || !end) return defaultPeriod(today);
  const period = { start, end };
  return isValidPeriod(period, today) ? period : defaultPeriod(today);
}

/** First day of the month, in the `start` form the insights endpoint takes. */
export function monthStartDate(month: string): string {
  return `${month}-01`;
}

/** Last day of the month, in the `end` form the insights endpoint takes. */
export function monthEndDate(month: string): string {
  return `${month}-${pad2(daysInMonth(month))}`;
}

/**
 * The `end` date to query for a period. A period ending in a month we intend to
 * project is asked for only up to its last complete day, so the days the server
 * aggregates are exactly the days projectionFactor scales — querying to the
 * month's end would sweep in today's partial takings and overstate the rate.
 */
export function periodEndDate(
  period: MonthPeriod,
  today: Date = new Date()
): string {
  if (projectionFactor(period.end, today) == null) {
    return monthEndDate(period.end);
  }
  return `${period.end}-${pad2(projectableDays(today))}`;
}

/**
 * Months of volume a period's window actually holds — fractional when it ends
 * in a month being projected, where the window stops after a whole number of
 * weeks.
 *
 * This is the correction the server's own averages need. It divides period
 * totals by the calendar months its window *touches*, which equals a period's
 * month span and is only the right divisor when every month in it is complete.
 * Equal to the span for a period of complete months, so the correction is then
 * exactly 1 and those periods are left alone.
 */
export function effectiveMonths(
  period: MonthPeriod,
  today: Date = new Date()
): number {
  const factor = projectionFactor(period.end, today);
  const span = monthSpan(period);
  // 1 / factor is the fraction of the final month the window covers.
  return factor == null ? span : span - 1 + 1 / factor;
}

export function containsMonth(period: MonthPeriod, month: string): boolean {
  return month >= period.start && month <= period.end;
}
